import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { getKnowledgeLibraryFamily, knowledgeLibraryErrorMessage } from '../../Api/KnowledgeLibraryApi';
import { legalStatusLabel, shortDate } from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';
import { inForceVersion, splitFamily } from './knowledgeHelpers';

const dash = (v) => shortDate(v) || '-';

/** Every version and amendment of one law, with status and dates; the version in force is marked. */
export default function KnowledgeFamilyDialog({ open, familyKey, title, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!open || !familyKey) return undefined;
    setRows(null);
    setError('');
    getKnowledgeLibraryFamily(familyKey)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        setRows(Array.isArray(data) ? data : data?.items || []);
      })
      .catch((e) => { if (!cancelled) setError(knowledgeLibraryErrorMessage(e, 'Could not load the versions')); });
    return () => { cancelled = true; };
  }, [open, familyKey]);

  const { versions, amendments } = splitFamily(rows || [], familyKey);
  const current = inForceVersion(versions);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="knowledge-family-title">
      <DialogTitle id="knowledge-family-title">{title || familyKey}: versions and amendments</DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!rows && !error ? <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box> : null}
        {rows ? (
          <>
            <Typography sx={{ fontWeight: 600, mt: 1, mb: 1 }}>Versions</Typography>
            <Table size="small" aria-label="Versions">
              <TableHead>
                <TableRow><TableCell>Version</TableCell><TableCell>Legal status</TableCell><TableCell>Effective from</TableCell><TableCell>Effective to</TableCell><TableCell>Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.id} data-testid={`version-${v.id}`}>
                    <TableCell>
                      {v.version_label || v.title}
                      {current && current.id === v.id ? <Chip size="small" label="Current version" color="success" sx={{ ml: 1, height: 20 }} /> : null}
                    </TableCell>
                    <TableCell>{legalStatusLabel(v.legal_status) || '-'}</TableCell>
                    <TableCell>{dash(v.effective_from)}</TableCell>
                    <TableCell>{dash(v.effective_to)}</TableCell>
                    <TableCell>{v.status}</TableCell>
                  </TableRow>
                ))}
                {!versions.length ? <TableRow><TableCell colSpan={5}>No versions yet.</TableCell></TableRow> : null}
              </TableBody>
            </Table>
            <Typography sx={{ fontWeight: 600, mt: 3, mb: 1 }}>Amendments</Typography>
            <Table size="small" aria-label="Amendments">
              <TableHead>
                <TableRow><TableCell>Title</TableCell><TableCell>Legal status</TableCell><TableCell>Commencement</TableCell><TableCell>Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {amendments.map((a) => (
                  <TableRow key={a.id} data-testid={`amendment-${a.id}`}>
                    <TableCell>{a.title}</TableCell>
                    <TableCell>{legalStatusLabel(a.legal_status) || '-'}</TableCell>
                    <TableCell>{dash(a.commencement_date)}</TableCell>
                    <TableCell>{a.status}</TableCell>
                  </TableRow>
                ))}
                {!amendments.length ? <TableRow><TableCell colSpan={4}>No amendments.</TableCell></TableRow> : null}
              </TableBody>
            </Table>
          </>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
    </Dialog>
  );
}
